import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { sectionChipStyles } from '@/lib/sectionTints';

/**
 * Wave 3.99k — Reusable sticky section-navigator chip bar.
 *
 * Given a list of ``{id, label, icon?}`` sections, renders a horizontal
 * scrollable chip bar that:
 *
 *   • Sticks below the AppShell top bar so it stays reachable as the
 *     user scrolls through a long detail page.
 *   • Highlights the "current" section using an IntersectionObserver
 *     that fires as each section crosses the viewport middle.
 *   • On click, smoothly scrolls the associated anchor into view
 *     (offsets to sit just under the sticky bar itself so nothing gets
 *     hidden behind it). Respects prefers-reduced-motion.
 *
 * Consumers just need to:
 *   1. Tag each of their `<section>` / `<div>` roots with a stable
 *      ``id`` (kebab-case).
 *   2. Pass a matching `sections` list into this component and drop it
 *      just under the page header.
 *
 * Design notes:
 *   • Chips are visually subtle — border + neutral text by default,
 *     ink-navy fill for the active chip so it reads clearly without
 *     drawing more attention than the section titles below.
 *   • On tablet / mobile, the row scrolls horizontally rather than
 *     wrapping — this keeps the bar to a single line and avoids
 *     forcing a layout shift on smaller screens.
 *   • Component intentionally does NOT scroll the page on mount; it
 *     only reacts to user clicks / scrolls.
 *
 * Usage::
 *
 *   const SECTIONS = [
 *     { id: 'order-header', label: 'Overview' },
 *     { id: 'order-files',  label: 'Files' },
 *     { id: 'order-requirements', label: 'Requirements' },
 *   ];
 *   <StickySectionNav sections={SECTIONS} topOffset={64} />
 */
export function StickySectionNav({
  sections = [],
  topOffset = 60,           // sticky offset from the top of the viewport
  extraScrollOffset = 12,   // gap between the sticky bar and section top when scrolled
  className = '',
  testId = 'sticky-section-nav',
}) {
  const [activeId, setActiveId] = useState(sections?.[0]?.id || null);
  // Track intersection ratios ourselves so we can pick the best-visible
  // section even when several are in view at once (a common case on
  // wide monitors where two half-height cards can overlap).
  const ratiosRef = useRef({});
  // Guard: after a click we lock the active section briefly so the
  // observer doesn't oscillate while the smooth-scroll is animating.
  const clickLockUntilRef = useRef(0);

  useEffect(() => {
    if (!sections?.length || typeof IntersectionObserver === 'undefined') return;

    const seen = new Set();
    const observed = [];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          ratiosRef.current[e.target.id] = e.isIntersecting ? e.intersectionRatio : 0;
        }
        // While a click-driven scroll is in progress, don't fight it.
        if (performance.now() < clickLockUntilRef.current) return;

        // Prefer the topmost intersecting section (closest to the
        // sticky bar) — matches user intuition better than "biggest
        // ratio" when multiple large sections overlap.
        let winnerId = null;
        let winnerTop = Infinity;
        for (const sec of sections) {
          const el = document.getElementById(sec.id);
          if (!el) continue;
          const ratio = ratiosRef.current[sec.id] || 0;
          if (ratio <= 0) continue;
          const rect = el.getBoundingClientRect();
          // Distance of the section top from the "reading line"
          // (just below the sticky bar). Negative means already
          // scrolled past the reading line; positive means still
          // below. We favour sections whose top is closest to the
          // reading line but not too far below it.
          const distFromLine = rect.top - (topOffset + 8);
          const effective = distFromLine >= 0 ? distFromLine : (-distFromLine) * 0.6;
          if (effective < winnerTop) {
            winnerTop = effective;
            winnerId = sec.id;
          }
        }
        if (winnerId) setActiveId(winnerId);
      },
      {
        // Trigger when any part of the section reaches the sticky bar
        // OR when it approaches from below — gives smooth active
        // handoff between adjacent sections.
        rootMargin: `-${topOffset + 8}px 0px -55% 0px`,
        threshold: [0, 0.05, 0.15, 0.3, 0.6],
      },
    );

    for (const s of sections) {
      const el = document.getElementById(s.id);
      if (el && !seen.has(s.id)) {
        seen.add(s.id);
        observer.observe(el);
        observed.push(el);
      }
    }
    return () => {
      for (const el of observed) {
        try { observer.unobserve(el); } catch (_) { /* element may already be gone */ }
      }
      observer.disconnect();
    };
    // ``sections`` identity change should rewire the observer.
  }, [sections, topOffset]);

  const jumpTo = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const y = el.getBoundingClientRect().top + window.scrollY - topOffset - extraScrollOffset;
      // Lock the observer for ~700ms so smooth-scroll doesn't cause
      // the highlighted chip to flicker across intermediate sections.
      clickLockUntilRef.current = performance.now() + 700;
      setActiveId(id);
      window.scrollTo({ top: y, behavior: prefersReduced ? 'auto' : 'smooth' });
    },
    [topOffset, extraScrollOffset],
  );

  if (!sections?.length) return null;

  return (
    <div
      data-testid={testId}
      className={cn(
        // Sticky positioning — hugs just under the AppShell top bar.
        'sticky z-30 -mx-4 md:-mx-6 px-4 md:px-6 py-2',
        // Soft translucent background so the underlying content
        // remains legible when it scrolls behind the bar.
        'bg-[color-mix(in_oklab,var(--paper-warm)_92%,white)] backdrop-blur',
        // Subtle divider so the bar visually detaches from content.
        'border-b border-[var(--divider)]',
        className,
      )}
      style={{ top: `${topOffset}px` }}
    >
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 pr-1 hidden md:inline">
          Jump to
        </span>
        {sections.map((s) => {
          const isActive = s.id === activeId;
          const chipStyles = sectionChipStyles(s.tint);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => jumpTo(s.id)}
              data-testid={`${testId}-chip-${s.id}`}
              className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap',
                'text-[12px] font-medium rounded-full px-2.5 py-1',
                'border transition-colors duration-150',
                isActive
                  ? (chipStyles?.active
                      ? cn(chipStyles.active, 'shadow-sm')
                      : 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)] shadow-sm')
                  : 'bg-white/70 text-slate-600 border-[var(--divider)] hover:bg-white hover:text-[var(--ink-navy)]',
              )}
            >
              {/* Wave 3.99n — Section colour dot on inactive chips so
                  operators can still spot the section colour without
                  the chip being active. The dot uses the same hue
                  family as the section body it links to. */}
              {chipStyles?.dot && !isActive && (
                <span
                  aria-hidden
                  className={cn('inline-block w-1.5 h-1.5 rounded-full', chipStyles.dot)}
                />
              )}
              {s.icon ? (
                <span className="inline-flex items-center" aria-hidden>
                  {s.icon}
                </span>
              ) : null}
              <span>{s.label}</span>
              {typeof s.badge !== 'undefined' && s.badge !== null && (
                <span
                  className={cn(
                    'ml-1 rounded-full px-1.5 text-[10px] leading-4 font-semibold',
                    isActive
                      ? (chipStyles?.active
                          ? 'bg-white/60 text-current'
                          : 'bg-white/25 text-white')
                      : 'bg-slate-100 text-slate-600',
                  )}
                >
                  {s.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default StickySectionNav;
